import React, {
	type ClipboardEvent,
	memo,
	useState,
	useRef,
	useCallback,
	useMemo,
	useEffect,
	type ElementRef,
	type KeyboardEvent,
	type SyntheticEvent,
	type ChangeEvent,
	useImperativeHandle,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
// eslint-disable-next-line jira/restricted/use-debounce
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuid } from 'uuid';
import Avatar from '@atlaskit/avatar';
import AddIcon from '@atlaskit/icon/glyph/add';
import Popup, { type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { AvatarLite } from '@atlassian/jira-business-avatar-lite/src/index.tsx';
import { DEFAULT_AVATAR_RES } from '@atlassian/jira-business-constants/src/index.tsx';
import { CellWrapper as CellWrapperNew } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { getPopupComponentWithHeight } from '@atlassian/jira-business-fields/src/common/ui/popup/index.tsx';
import { useAddPeople } from '@atlassian/jira-business-invite-people/src/controllers/add-people-provider/index.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type {
	UserValue,
	User as IssueFieldUser,
} from '@atlassian/jira-issue-field-assignee/src/common/types.tsx';
import messages from '@atlassian/jira-issue-field-assignee/src/messages.tsx';
import { saveAssigneeField } from '@atlassian/jira-issue-field-assignee/src/services/use-assignee-field/utils.tsx';
import useUsersQuery from '@atlassian/jira-issue-field-assignee/src/services/users-query/index.tsx';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import { ASSIGNEE_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import {
	CellWrapper,
	AvatarItem,
	DisplayName,
	DROPDOWN_HEIGHT,
} from '../../../common/ui/list-cell/styled.tsx';
import type { TransformedUser } from '../../../services/get-list-view-issues/types.tsx';
import ErrorUser from './error-user/index.tsx';
import userMessages from './messages.tsx';
import type { User, UserCellProps } from './types.tsx';

const SEARCH_DEBOUNCE_TIMEOUT = 300;
const EMPTY_USER_ID = '__EMPTY_USER_ID__';
const INVITE_PEOPLE_ID = '__INVITE_PEOPLE_ID__';
const MAX_NUMBER_OF_OPTIONS = 7;
const LISTBOX_OPTION_PREFIX = `user-option-${uuid()}`;
const LISTBOX_LIST_ID = `user-listbox-${uuid()}`;

const getOptionId = (index: number) => `${LISTBOX_OPTION_PREFIX}-${index}`;

const createTestId = (testId: string | null | undefined, suffix = '') =>
	testId != null ? `${testId}${suffix}` : undefined;

const getAvatarUrl = (user: IssueFieldUser) =>
	user.avatarUrls && user.avatarUrls[DEFAULT_AVATAR_RES] ? user.avatarUrls[DEFAULT_AVATAR_RES] : '';

const UserCell = memo<UserCellProps>(
	({
		initialValue,
		fieldKey,
		fieldType,
		issueKey,
		isEditable = true,
		width,
		emptyUserNameText,
		loggedInUserActionNameText,
		testId,
		isSelected = false,
		wrapperRef,
		onFocus,
		onBlur,
		onKeyDown,
		editMode: editModeNew = false,
		setEditMode: setEditModeNew = noop,
		isHovering: isHoveringNew = false,
		cellRef,
		fieldName = '',
		issueType = '',
		issueTypeId = '',
		inputValue,
		onInputChange = noop,
	}: UserCellProps) => {
		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { openAddPeopleModal } = useAddPeople();
		const userQuery = useRef<{
			previousQuery?: string;
		}>({ previousQuery: undefined }).current;
		const [{ data: users, loading: loadingUsers, error }, fetchUsers] = useUsersQuery(
			`/rest/api/3/user/assignable/search?issueKey=${issueKey}&maxResults=${MAX_NUMBER_OF_OPTIONS}&query=`,
		);

		const emptyUserName = formatMessage(emptyUserNameText);
		const listboxLabel = formatMessage(userMessages.assigneeList);
		const emptyUser = useMemo(
			() => ({
				accountId: EMPTY_USER_ID,
				displayName: formatMessage(messages.unassignedOption),
				avatarUrls: {},
			}),
			[formatMessage],
		);
		const invitePeople = useMemo(
			() => ({
				accountId: INVITE_PEOPLE_ID,
				displayName: formatMessage(messages.invitePeopleOptionAdmin),
				avatarUrls: {},
			}),
			[formatMessage],
		);
		const initialValueUser = useMemo(() => {
			if (initialValue) {
				return {
					accountId: initialValue.accountId,
					displayName:
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						(initialValue as TransformedUser)?.name ||
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						(initialValue as User).displayName,
					avatarUrls: {
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						[DEFAULT_AVATAR_RES]: (initialValue as TransformedUser)?.picture,
					},
				};
			}
			return null;
		}, [initialValue]);

		const [{ value: assigneeValue, error: updateError }, { saveValue }] = useEditField<
			UserValue,
			null
		>({
			fieldKey,
			issueKey,
			fieldType,
			initialValue: initialValueUser,
			...(fieldKey === ASSIGNEE_TYPE ? { saveField: saveAssigneeField } : {}),
		});

		const value = fg('jwm_list_new_copy_paste_behaviour')
			? assigneeValue
			: assigneeValue ?? initialValueUser;

		const [editMode, setEditMode] = fg('jira_list_keyboard_nav')
			? [editModeNew, setEditModeNew]
			: // eslint-disable-next-line react-hooks/rules-of-hooks
				useState<boolean>(false);
		const [highlightedUserIndex, setHighlightedUserIndex] = useState(0);
		const [activeDescendentId, setActiveDescendentId] = useState('');
		const currentUserAccountId = useAccountId();
		const [isHoveringOld, setIsHovering] = useState(false);
		const isHovering = fg('jira_list_keyboard_nav') ? isHoveringNew : isHoveringOld;
		const [isStartSearching, setIsStartSearching] = useState(false);
		const [userValue, setUserValue] = useState<UserValue>(null);
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const currentUser = useRef(useCurrentUser().data.user as IssueFieldUser);
		const [userDisplayName, setUserDisplayName] = useState(userValue?.displayName ?? emptyUserName);
		const [textValueOld, setTextValueOld] = useState(userDisplayName);

		const textValue = fg('jira_list_keyboard_nav') ? inputValue ?? userDisplayName : textValueOld;
		const setTextValue = fg('jira_list_keyboard_nav') ? onInputChange : setTextValueOld;

		const [usersList, setUsersList] = useState<IssueFieldUser[]>([]);
		const [fetchedUsers, setFetchedUsers] = useState(false);
		const isUnassigned = !userValue?.accountId;
		const selectedAvatarUrl = userValue?.displayName === textValue ? getAvatarUrl(userValue) : '';

		const userOptions = useMemo(() => {
			const isAssigneeFieldType = fieldKey === ASSIGNEE_TYPE && isStartSearching;

			if (isAssigneeFieldType) {
				// if search has started and, we have less than 3 users, we need to show invite people option
				const invitePeopleOption = usersList.length <= 2 ? [invitePeople] : [];
				const emptyOption = textValue === '' ? [emptyUser] : [];

				return [
					...emptyOption,
					...usersList.slice(0, Math.min(MAX_NUMBER_OF_OPTIONS, usersList.length + 1)),
					...invitePeopleOption,
				];
			}

			// If we are here, it means there is no query for this field, so we show the default values
			if (usersList.length === 0) {
				return [emptyUser];
			}

			// it's assigned to the current user
			return [
				...(isUnassigned || value?.accountId !== currentUserAccountId ? [currentUser.current] : []),
				...(isUnassigned ? [] : [emptyUser]),
				...usersList
					.filter((u) => u.accountId !== currentUser.current.accountId)
					.slice(0, Math.min(MAX_NUMBER_OF_OPTIONS, usersList.length + 1)),
			];
		}, [
			fieldKey,
			isStartSearching,
			usersList,
			textValue,
			invitePeople,
			emptyUser,
			currentUserAccountId,
			isUnassigned,
			value?.accountId,
		]);

		useEffect(() => {
			setTextValue(userDisplayName);
		}, [setTextValue, userDisplayName]);

		useEffect(() => {
			setUserValue(value);
			setUserDisplayName(value?.displayName ?? emptyUserName);
		}, [value, emptyUserName]);

		useEffect(() => {
			setUsersList(users);
		}, [users]);

		useEffect(() => {
			if (editMode && isEditable && !fetchedUsers && fg('jira_list_keyboard_nav')) {
				setFetchedUsers(true);
				fetchUsers();
			}
		}, [usersList, editMode, isEditable, fetchUsers, fetchedUsers]);

		const handleUserOptionClick = useCallback(
			(user: IssueFieldUser) => (event: SyntheticEvent) => {
				event.stopPropagation();
				setEditMode(false);
				setIsStartSearching(false);
				const isInvitePeopleID = user.accountId === INVITE_PEOPLE_ID;
				if (isInvitePeopleID) {
					openAddPeopleModal(undefined, 'listInviteFromAssigneeBtn');
				}
				setUserDisplayName(isInvitePeopleID ? '' : user.displayName);
				setTextValue(isInvitePeopleID ? '' : user.displayName);
				saveValue(
					user.accountId === EMPTY_USER_ID || isInvitePeopleID ? null : user,
					null,
					createAnalyticsEvent({}),
				);
				// settimeout used here as focus is lost due to Textfield blur event
				setTimeout(() => {
					wrapperRef?.current?.focus();
				});
			},
			[setEditMode, setTextValue, saveValue, createAnalyticsEvent, openAddPeopleModal, wrapperRef],
		);

		const [handleSearch] = useDebouncedCallback((query?: string) => {
			query !== userQuery.previousQuery && fetchUsers(query);
			userQuery.previousQuery = query;
			setHighlightedUserIndex(0);
		}, SEARCH_DEBOUNCE_TIMEOUT);

		const handleTextChange = useCallback(
			(event: ChangeEvent<HTMLInputElement>) => {
				const newValue = event.target.value;
				setTextValue(newValue);
				setIsStartSearching(true);
				handleSearch(newValue);
			},
			[handleSearch, setTextValue],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled through useEffect above
		 */
		const handleCellClick = useCallback(() => {
			if (!editMode && isEditable) {
				setEditMode(true);
				(!usersList || !usersList.length) && fetchUsers();
			}
		}, [editMode, isEditable, usersList, fetchUsers, setEditMode]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled through handleKeyDownNew
		 */
		const handleKeyPressOnSearchField = useCallback(
			(e: KeyboardEvent<HTMLElement>) => {
				if (fg('jira_list_keyboard_nav')) {
					return;
				}

				switch (e.key) {
					case 'Escape': {
						e.preventDefault();
						setEditMode(false);
						if (value) {
							setTextValue(value.displayName);
						}
						wrapperRef?.current?.focus();
						break;
					}
					case 'ArrowUp': {
						e.preventDefault();
						const activeIndex =
							highlightedUserIndex === 0 ? userOptions.length - 1 : highlightedUserIndex - 1;
						setHighlightedUserIndex(activeIndex);
						break;
					}
					case 'ArrowDown': {
						e.preventDefault();
						const activeIndex =
							highlightedUserIndex === userOptions.length - 1 ? 0 : highlightedUserIndex + 1;
						setHighlightedUserIndex(activeIndex);
						break;
					}
					case 'Enter': {
						e.preventDefault();
						userOptions[highlightedUserIndex] &&
							handleUserOptionClick(userOptions[highlightedUserIndex])(e);
						break;
					}
					default: {
						break;
					}
				}
			},
			[
				setEditMode,
				value,
				wrapperRef,
				setTextValue,
				highlightedUserIndex,
				userOptions,
				handleUserOptionClick,
			],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated wrapperRef is now managed in CellNavigation
		 */
		const onWrapperRef = useCallback(
			(ref: ElementRef<'div'> | null) => {
				if (wrapperRef) {
					// eslint-disable-next-line no-param-reassign
					wrapperRef.current = ref;
				}
			},
			[wrapperRef],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleMouseEnter in CellNavigation for generic behaviour or useImperativeHandle
		 *  to provide custom override behaviour for this cell
		 */
		const handleMouseLeave = useCallback(() => {
			setIsHovering(false);
		}, []);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleMouseLeave in CellNavigation for generic behaviour or useImperativeHandle
		 *  to provide custom override behaviour for this cell
		 */
		const handleMouseEnter = useCallback(() => {
			setIsHovering(true);
		}, []);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleKeyDown in CellNavigation for generic behaviour or handleKeyDownNew in this
		 * cell for custom behaviour
		 */
		const handleKeyDown = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				e.stopPropagation();
				!editMode && onKeyDown?.(e);
			},
			[editMode, onKeyDown],
		);

		const handleKeyDownNew = useCallback(
			(e: KeyboardEvent<HTMLElement>) => {
				if (!(e.target instanceof HTMLElement) || e.target.nodeName !== 'INPUT') {
					return;
				}

				switch (e.code) {
					case 'ArrowUp': {
						const activeIndex =
							highlightedUserIndex === 0 ? userOptions.length - 1 : highlightedUserIndex - 1;
						setHighlightedUserIndex(activeIndex);
						return true;
					}
					case 'ArrowDown': {
						const activeIndex =
							highlightedUserIndex === userOptions.length - 1 ? 0 : highlightedUserIndex + 1;
						setHighlightedUserIndex(activeIndex);
						return true;
					}
					case 'Enter': {
						userOptions[highlightedUserIndex] &&
							handleUserOptionClick(userOptions[highlightedUserIndex])(e);
						return false;
					}
					default: {
						return false;
					}
				}
			},
			[userOptions, handleUserOptionClick, highlightedUserIndex],
		);

		const handlePopupClose = useCallback(() => {
			setEditMode(false);
			setIsStartSearching(false);
			setTextValue(userDisplayName);
			setHighlightedUserIndex(0);
		}, [setEditMode, setTextValue, userDisplayName]);

		const handleTextRef = useCallback((textFieldRef: HTMLInputElement | null) => {
			// Workaround to force close any other open overlays
			textFieldRef?.click();
			textFieldRef?.focus();
		}, []);

		const handleCutCopy = useCallback(
			(event: ClipboardEvent<HTMLElement>) => {
				event.preventDefault();
				if (value) {
					// copy plain text so can paste readable format anywhere
					event.clipboardData.setData('text/plain', value.displayName);
					// copy html as we need to know the accountId incase there are multiple users with same name
					event.clipboardData.setData(
						'text/html',
						`<span account-id="${value.accountId}">${value.displayName}</span>`,
					);
				}

				if (event.type === 'copy') {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell copied', 'user');
				} else if (event.type === 'cut') {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell cut', 'user');
					// clear cell content
					setTextValue('');
					saveValue(null, null, createAnalyticsEvent({}));
				}
			},
			[value, createAnalyticsEvent, setTextValue, saveValue],
		);

		const handlePaste = useCallback(
			(event: ClipboardEvent<HTMLElement>) => {
				event.preventDefault();
				const clipHtml = event.clipboardData.getData('text/html');
				const clipText = event.clipboardData.getData('text/plain');

				if (clipHtml) {
					const parser = new DOMParser();
					const htmlDoc = parser.parseFromString(clipHtml, 'text/html');
					const accountId = htmlDoc.querySelector('span')?.getAttribute('account-id');
					const displayName = htmlDoc.querySelector('span')?.textContent;

					if (displayName) {
						setTextValue(displayName);
					}

					if (accountId) {
						performGetRequest(
							`/rest/api/2/user/assignable/search?issueKey=${issueKey}&accountId=${accountId}`,
						).then((response: IssueFieldUser[] | null | undefined) => {
							if (Array.isArray(response) && response.length > 0) {
								setUsersList(response);
								setHighlightedUserIndex(1);
							}
						});
						fireUIAnalytics(createAnalyticsEvent({}), 'cell pasted', 'user', {
							format: 'html',
						});
					}
				} else if (clipText) {
					setTextValue(clipText);
					fireUIAnalytics(createAnalyticsEvent({}), 'cell pasted', 'user', {
						format: 'text',
					});
				}
			},
			[setTextValue, issueKey, createAnalyticsEvent],
		);

		/**
		 * Static cell renderer (without hovering): minimum HTML to improve page load.
		 * We use Atlaskit avatar for hover/active state as it renders SVG for Unassigned state
		 */
		const staticContent = useMemo(
			() =>
				isUnassigned ? null : (
					<>
						<AvatarLite avatarUrl={selectedAvatarUrl} testId={createTestId(testId, '--avatar')} />
						<DisplayName data-testid={createTestId(testId, '--name')}>
							{userDisplayName}
						</DisplayName>
					</>
				),
			[isUnassigned, selectedAvatarUrl, testId, userDisplayName],
		);

		const userDisplayLabel = useCallback(
			() => (
				<Tooltip content={userDisplayName}>
					<DisplayName data-testid={createTestId(testId, '--name')}>{userDisplayName}</DisplayName>
				</Tooltip>
			),
			[testId, userDisplayName],
		);

		const popupTrigger = useCallback(
			({ ref, ...triggerProps }: TriggerProps) => (
				<AvatarItem
					isUnassigned={isUnassigned && !editMode}
					ref={ref}
					{...triggerProps}
					data-testid="business-list.ui.list-view.user-cell.popup-avatar-item"
				>
					<Avatar appearance="circle" src={selectedAvatarUrl} size="small" />
					{editMode ? (
						<Box xcss={userSearchWrapperStyles}>
							<Textfield
								appearance="none"
								value={textValue === emptyUserName ? '' : textValue}
								onChange={handleTextChange}
								placeholder={emptyUserName}
								ref={handleTextRef}
								onKeyDown={handleKeyPressOnSearchField}
								role="combobox"
								aria-expanded
								aria-autocomplete="list"
								aria-controls={LISTBOX_LIST_ID}
								aria-activedescendant={activeDescendentId}
								data-testid={createTestId(testId, '--user-search-input')}
							/>
						</Box>
					) : (
						userDisplayLabel()
					)}
				</AvatarItem>
			),
			[
				isUnassigned,
				editMode,
				selectedAvatarUrl,
				textValue,
				emptyUserName,
				handleTextChange,
				handleTextRef,
				handleKeyPressOnSearchField,
				activeDescendentId,
				testId,
				userDisplayLabel,
			],
		);

		const popupContent = useCallback(
			() => (
				<UsersContainer width={width} role="listbox" id={LISTBOX_LIST_ID} aria-label={listboxLabel}>
					{userOptions.map((user, index) => {
						const id = getOptionId(index);
						const isInvitePeople = user.accountId === INVITE_PEOPLE_ID;
						const shouldHighlight = index === highlightedUserIndex;
						if (shouldHighlight && activeDescendentId !== id) {
							setActiveDescendentId(id);
						}
						return (
							<UserOptionWrapper
								id={id}
								key={user.accountId}
								onClick={handleUserOptionClick(user)}
								highlighted={shouldHighlight}
								data-testid={createTestId(testId, `--option-${index}`)}
								role="option"
								aria-selected={shouldHighlight}
							>
								{isInvitePeople ? (
									<Box xcss={iconWrapperStyles}>
										<AddIcon label="Add" size="medium" />
									</Box>
								) : (
									<Avatar appearance="circle" src={getAvatarUrl(user)} size="small" />
								)}
								<DisplayName>
									{user.displayName ?? emptyUserName}
									{user.accountId === currentUserAccountId && (
										<AdditionalInfo>
											{
												' ' /* the space here is important for accessibility, it separates the words */
											}
											({formatMessage(loggedInUserActionNameText)})
										</AdditionalInfo>
									)}
								</DisplayName>
							</UserOptionWrapper>
						);
					})}

					{loadingUsers && (
						<SpinnerWrapper data-testid="business-list.ui.list-view.user-cell.spinner-wrapper">
							<Spinner />
						</SpinnerWrapper>
					)}

					{error && <ErrorUser onFetchUsers={fetchUsers} />}
				</UsersContainer>
			),
			[
				activeDescendentId,
				currentUserAccountId,
				emptyUserName,
				error,
				fetchUsers,
				formatMessage,
				handleUserOptionClick,
				highlightedUserIndex,
				listboxLabel,
				loadingUsers,
				loggedInUserActionNameText,
				testId,
				userOptions,
				width,
			],
		);

		const popupOffset: [number, number] = useMemo(() => [-gridSize, gridSize], []);

		const popupComponent = useMemo(
			() =>
				getPopupComponentWithHeight({
					numOptions: Math.min(userOptions.length, MAX_NUMBER_OF_OPTIONS),
					spacing: 'default',
					hasInput: false,
				}),
			[userOptions],
		);

		// hover/active state
		const content = useMemo(
			() =>
				isHovering || editMode ? (
					<Popup
						isOpen={editMode}
						placement="bottom-start"
						onClose={handlePopupClose}
						autoFocus={false}
						trigger={popupTrigger}
						content={popupContent}
						offset={popupOffset}
						popupComponent={popupComponent}
					/>
				) : (
					staticContent
				),
			[
				handlePopupClose,
				isHovering,
				editMode,
				staticContent,
				popupTrigger,
				popupContent,
				popupOffset,
				popupComponent,
			],
		);

		const cellOverrides = useMemo(
			() => () => ({
				onKeyDown: handleKeyDownNew,
			}),
			[handleKeyDownNew],
		);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		if (!isEditable && value?.accountId == null) {
			return null;
		}

		const userCell = (
			<>
				{isEditable ? content : staticContent}
				{updateError && (
					<FixItErrorFlag
						error={updateError}
						title={userMessages.errorTitle}
						description={userMessages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		if (fg('jira_list_keyboard_nav')) {
			return (
				<CellWrapperNew
					isEditable={isEditable}
					testId={createTestId(testId, '--cell-wrapper')}
					{...(!fg('jwm_list_new_copy_paste_behaviour')
						? {
								onCopy: handleCutCopy,
								onCut: handleCutCopy,
								onPaste: handlePaste,
							}
						: {})}
				>
					{userCell}
				</CellWrapperNew>
			);
		}

		return (
			<CellWrapper
				isFocused={editMode || isSelected}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onClick={handleCellClick}
				isEditable={isEditable}
				onBlur={onBlur}
				tabIndex={0}
				onFocus={onFocus}
				onKeyDown={handleKeyDown}
				ref={onWrapperRef}
				onCopy={handleCutCopy}
				onCut={handleCutCopy}
				onPaste={handlePaste}
				data-testid={createTestId(testId, '--cell-wrapper')}
			>
				{userCell}
			</CellWrapper>
		);
	},
);

export default UserCell;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AdditionalInfo = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N50),
});

const iconWrapperStyles = xcss({
	margin: 'space.025',
});

const userSearchWrapperStyles = xcss({
	marginLeft: 'space.negative.025',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UsersContainer = styled.div<{ width: string | number }>({
	minWidth: '240px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: `${({ width }: { width: string | number }) => width}px`,
	backgroundColor: token('elevation.surface', 'white'),
	overflow: 'hidden',
	boxShadow: token(
		'elevation.shadow.raised',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`0 1px 1px ${colors.N50A}, 0 0 1px 1px ${colors.N40A}`,
	),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UserOptionWrapper = styled.div<{ highlighted: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${DROPDOWN_HEIGHT}px`,
	display: 'flex',
	paddingTop: 0,
	paddingRight: token('space.100', '8px'),
	paddingBottom: 0,
	paddingLeft: token('space.100', '8px'),
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > :last-child': {
		paddingLeft: token('space.050', '4px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ highlighted }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		highlighted ? `${token('color.background.accent.gray.subtler', colors.B50)}` : 'inherit',
	cursor: 'pointer',
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: ({ highlighted }) =>
			highlighted
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					token('color.background.accent.gray.subtlest', colors.B50)
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					token('color.background.accent.gray.subtlest', colors.N20A),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${DROPDOWN_HEIGHT}px`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});
